import { useEffect, useMemo, useRef, useState } from "react";
import { X, Search, ChevronLeft, ChevronRight, Download, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { GUIDE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const LS_KEY_LAST = (userId) => `mss_guide_last_chapter_${userId || "anon"}`;

export default function GuidePanel({ open, onClose }) {
    const { user } = useAuth();
    const [chapters, setChapters] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/docs/user-guide")
            .then(({ data }) => {
                setChapters(data.chapters || []);
                const saved = localStorage.getItem(LS_KEY_LAST(user?.id));
                const initial = (data.chapters || []).find((c) => c.id === saved) || (data.chapters || [])[0];
                if (initial) setActiveId(initial.id);
            })
            .catch(() => toast.error("Could not load the guide"))
            .finally(() => setLoading(false));
    }, [open, user?.id]);

    useEffect(() => {
        if (open && activeId) {
            localStorage.setItem(LS_KEY_LAST(user?.id), activeId);
            if (bodyRef.current) bodyRef.current.scrollTop = 0;
        }
    }, [activeId, open, user?.id]);

    const filtered = useMemo(() => {
        if (!query.trim()) return chapters;
        const q = query.trim().toLowerCase();
        return chapters.filter((c) => c.search?.includes(q) || c.title.toLowerCase().includes(q));
    }, [chapters, query]);

    const active = useMemo(() => chapters.find((c) => c.id === activeId) || null, [chapters, activeId]);
    const activeIdx = chapters.findIndex((c) => c.id === activeId);
    const hasPrev = activeIdx > 0;
    const hasNext = activeIdx >= 0 && activeIdx < chapters.length - 1;

    const downloadPdf = async () => {
        try {
            const res = await api.get("/docs/user-guide.pdf", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            toast.error("Could not open the PDF");
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                data-testid={GUIDE.panel}
                className="absolute right-0 top-0 bottom-0 bg-white shadow-2xl w-full max-w-[1080px] flex flex-col"
                style={{ borderLeft: "3px solid var(--kp-red)" }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-9 h-9" style={{ background: "var(--kp-red)", borderRadius: "2px" }}>
                            <BookOpen size={17} className="text-white" />
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                                MSS PNG Payroll · Operator manual
                            </div>
                            <div className="font-display font-bold text-xl text-[color:var(--kp-charcoal)] leading-tight">
                                {active?.title || "User guide"}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid={GUIDE.downloadPdfBtn}
                            onClick={downloadPdf}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.14em] text-neutral-600 hover:text-black border border-neutral-300 hover:border-neutral-500 rounded-[3px]"
                            title="Download the full PDF"
                        >
                            <Download size={13} /> PDF
                        </button>
                        <button
                            data-testid={GUIDE.closeBtn}
                            onClick={onClose}
                            className="p-2 text-neutral-500 hover:text-black"
                            aria-label="Close guide"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body: chapter nav + prose */}
                <div className="flex-1 min-h-0 flex">
                    <aside className="w-[260px] shrink-0 border-r border-neutral-200 bg-[#FAFAFA] flex flex-col">
                        <div className="p-3 border-b border-neutral-200">
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" />
                                <input
                                    data-testid={GUIDE.searchInput}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search…"
                                    className="kp-focus w-full pl-7 pr-3 py-2 text-[12px] border border-neutral-300 bg-white rounded-[3px]"
                                />
                            </div>
                        </div>
                        <ol className="flex-1 overflow-y-auto py-2">
                            {loading && <li className="px-4 py-2 text-xs text-neutral-500">Loading chapters…</li>}
                            {!loading && filtered.length === 0 && (
                                <li className="px-4 py-3 text-xs text-neutral-500">No chapters match &ldquo;{query}&rdquo;.</li>
                            )}
                            {filtered.map((c) => (
                                <li key={c.id}>
                                    <button
                                        data-testid={GUIDE.chapterLink(c.id)}
                                        onClick={() => setActiveId(c.id)}
                                        className={`w-full text-left px-4 py-2.5 text-[12px] leading-tight border-l-2 transition-colors ${
                                            activeId === c.id
                                                ? "bg-white border-[color:var(--kp-red)] text-[color:var(--kp-charcoal)]"
                                                : "border-transparent text-neutral-600 hover:text-black hover:bg-white"
                                        }`}
                                    >
                                        <div className="font-mono-money text-[10px] text-[color:var(--kp-red)]">
                                            {String(c.num).padStart(2, "0")}
                                        </div>
                                        <div className="mt-0.5 font-semibold text-[12.5px]">{c.title}</div>
                                        <div className="text-[10px] text-neutral-500 mt-0.5 uppercase tracking-[0.14em] font-display">
                                            {c.eyebrow}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </aside>

                    <div ref={bodyRef} data-testid={GUIDE.body} className="flex-1 overflow-y-auto">
                        {active && (
                            <article className="max-w-[720px] px-8 py-8 mx-auto guide-prose">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                                    Chapter {String(active.num).padStart(2, "0")} · {active.eyebrow}
                                </div>
                                <h1 className="font-display font-bold text-3xl mt-1 text-[color:var(--kp-charcoal)]">{active.title}</h1>
                                <div className="h-[2px] w-[80px] mt-3 mb-6" style={{ background: "var(--kp-red)" }} />
                                <div dangerouslySetInnerHTML={{ __html: active.body_html }} />
                            </article>
                        )}
                    </div>
                </div>

                {/* Footer nav */}
                <div className="flex items-center justify-between px-6 py-3 border-t border-neutral-200 bg-white">
                    <button
                        data-testid={GUIDE.prevBtn}
                        disabled={!hasPrev}
                        onClick={() => hasPrev && setActiveId(chapters[activeIdx - 1].id)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.14em] text-neutral-600 hover:text-black disabled:opacity-30"
                    >
                        <ChevronLeft size={14} /> Prev
                    </button>
                    <div className="text-[11px] text-neutral-500 font-mono-money">
                        {activeIdx >= 0 ? `${activeIdx + 1} / ${chapters.length}` : ""}
                    </div>
                    <button
                        data-testid={GUIDE.nextBtn}
                        disabled={!hasNext}
                        onClick={() => hasNext && setActiveId(chapters[activeIdx + 1].id)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px] disabled:opacity-30"
                        style={{ background: "var(--kp-red)" }}
                    >
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
}
